import { defineStore } from "pinia";
import { groupBy } from "lodash";
import { sum } from "@/libs/amount.ts";
import date from "@/libs/date.ts";
import { TransactionFactory, type TransactionResource } from "@/factories/TransactionFactory.ts";
import { useAccount } from "@/stores/account.ts";
import { useReservations } from "@/stores/reservations.ts";
import type Transaction from "@/classes/Transaction.ts";
import type { ByDay, ByMonth, ByName, ByNameByDay, ByPeriod, Totals } from "@/types/transaction";

// pinia's state type is unwrapped on the store, which drops the class internals (RRule,
// Dayjs methods) the aggregation helpers rely on. Re-assert it here, once.
const stateAll = (state: { all: unknown }): Transaction[] => state.all as unknown as Transaction[];

// lodash types groupBy's values as the element type even though it returns arrays.
const groups = (transactions: Transaction[], key: (transaction: Transaction) => string) =>
  groupBy(transactions, key) as unknown as Record<string, Transaction[]>;

// Month ("YYYY-MM") and day ("YYYY-MM-DD") aggregation are the same walk; only the key
// format differs. Sharing it keeps the week view's balance maths identical to the month's.
// Reservations come from the backend (`useReservations`); this walk stays gross cash.
const periodsWithTotals = (transactions: Transaction[], format: string): ByPeriod => {
  const account = useAccount();

  let balance = account.initialBalance;

  const grouped = groups(chronological(transactions), (transaction: Transaction) =>
    date(transaction.date).format(format),
  );

  const periods: ByPeriod = {};

  for (const period in grouped) {
    const items = grouped[period];

    const txIn = sum(items.filter((tx: Transaction) => tx.type === "income"));

    const txOut = sum(items.filter((tx: Transaction) => tx.type === "expense"));

    balance += txIn - txOut;

    const totals: Totals = {
      in: txIn,
      out: txOut,
      balance,
    };

    periods[period] = {
      id: 0,
      isEnabled: true,
      transactions: items,
      totals,
    };
  }

  return periods;
};

// Per-name rows only need the transactions (totals stay zeroed, as before).
const periodsOf = (transactions: Transaction[], format: string): ByPeriod => {
  const grouped = groups(transactions, (transaction: Transaction) =>
    date(transaction.date).format(format),
  );

  const periods: ByPeriod = {};

  for (const period in grouped) {
    periods[period] = {
      id: 0,
      isEnabled: true,
      transactions: grouped[period],
      totals: {
        in: 0,
        out: 0,
        balance: 0,
      },
    };
  }

  return periods;
};

// Only `setAll` sorts; `fillFromResources` keeps whatever order the API returned, so the walk
// that accumulates balances re-sorts its own copy rather than trusting the caller's order.
const chronological = (transactions: Transaction[]): Transaction[] =>
  [...transactions].sort((a, b) => a.date.valueOf() - b.date.valueOf());

const sortedNames = (grouped: Record<string, Transaction[]>): string[] =>
  Object.keys(grouped).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

// Row metadata comes from the first transaction of the first period, as before.
const rowOf = (periods: ByPeriod) => {
  const tx = periods[Object.keys(periods)[0]].transactions[0];

  return { id: tx.id!, isEnabled: !tx.isDisabled, type: tx.type };
};

export const useTransactions = defineStore("Transactions", {
  state: () => ({
    all: [] as Transaction[],
  }),

  getters: {
    byDay(state): ByDay {
      return periodsWithTotals(stateAll(state), "YYYY-MM-DD");
    },

    byMonth(state): ByMonth {
      return periodsWithTotals(stateAll(state), "YYYY-MM");
    },

    byNameByMonth(state): ByName {
      const groupedByName = groups(stateAll(state), (transaction: Transaction) => transaction.name);

      const byName: ByName = {};

      for (const name of sortedNames(groupedByName)) {
        const transactionsPerMonth = periodsOf(groupedByName[name], "YYYY-MM");

        byName[name] = { ...rowOf(transactionsPerMonth), transactionsPerMonth };
      }

      return byName;
    },

    byNameByDay(state): ByNameByDay {
      const groupedByName = groups(stateAll(state), (transaction: Transaction) => transaction.name);

      const byName: ByNameByDay = {};

      for (const name of sortedNames(groupedByName)) {
        const transactionsPerDay = periodsOf(groupedByName[name], "YYYY-MM-DD");

        byName[name] = { ...rowOf(transactionsPerDay), transactionsPerDay };
      }

      return byName;
    },
  },

  actions: {
    setAll(transactions: Transaction[]) {
      // Sort a copy: the caller's array is not ours to reorder.
      this.all = [...transactions].sort((a, b) => a.date.valueOf() - b.date.valueOf());
      this.calculateBalances();
    },

    calculateBalances() {
      const account = useAccount();

      account.runningBalance = account.initialBalance;

      for (const tx of this.all) {
        tx.balance = account.getBalanceAfterTransaction(tx as Transaction);
      }

      void useReservations().refresh();
    },

    fillFromResources(resources: TransactionResource[]) {
      const account = useAccount();

      // `getBalanceAfterTransaction` projects forwards from wherever the balance sits, so seed it
      // first: otherwise each load carries on from the previous walk instead of the account balance.
      account.runningBalance = account.initialBalance;

      this.all = resources.map((transaction: TransactionResource) => {
        const tx = new TransactionFactory().fromJson(transaction);

        return {
          ...tx,
          balance: account.getBalanceAfterTransaction(tx),
        };
      });
    },

    forget(deletedTx: Transaction) {
      this.all = this.all.filter((tx) => tx.id !== deletedTx.id);
      this.calculateBalances();
    },

    insert(tx: Transaction) {
      for (const [idx, item] of this.all.entries()) {
        if (date(item.date).isAfter(date(tx.date))) {
          this.all.splice(idx, 0, tx);
          this.calculateBalances();

          return;
        }
      }

      this.all.push(tx);

      this.calculateBalances();
    },

    toggle(id: number, isDisabled: boolean) {
      const tx = this.all.find((tx) => tx.id === id);

      tx!.isDisabled = isDisabled;

      this.calculateBalances();
    },

    update(tx: Transaction) {
      this.all[this._find(tx)] = tx;

      // An edited date can move the transaction in time; the balance walk assumes chronology.
      this.all.sort((a, b) => a.date.valueOf() - b.date.valueOf());

      this.calculateBalances();
    },

    _find(tx: Transaction): number {
      return this.all.findIndex((t) => t.id === tx.id);
    },
  },
});
